import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const LOGO_CONFIGS = [
  {
    id: 'favicon-ico',
    fileName: 'favicon.ico',
    label: 'Favicon (ICO)',
    description: 'Tarayıcı sekmelerinde ve yer imlerinde görünen ana favicon dosyası. Tüm modern tarayıcılar tarafından desteklenir.',
    usageNote: 'Tarayıcı sekmesi, yer imleri, adres çubuğu',
    icon: 'Globe',
    accept: '.ico',
    dimensions: 'Çoklu boyut (16x16, 32x32, 48x48)',
    color: 'bg-blue-50 border-blue-200 text-blue-700',
    iconBg: 'bg-blue-100 text-blue-600',
  },
  {
    id: 'favicon-16',
    fileName: 'favicon-16x16.png',
    label: 'Favicon 16x16',
    description: 'Küçük tarayıcı sekme ikonları için kullanılan en küçük favicon boyutu.',
    usageNote: 'Tarayıcı sekme ikonu (küçük)',
    icon: 'Monitor',
    accept: '.png',
    dimensions: '16 × 16 piksel',
    color: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    iconBg: 'bg-indigo-100 text-indigo-600',
  },
  {
    id: 'favicon-32',
    fileName: 'favicon-32x32.png',
    label: 'Favicon 32x32',
    description: 'Standart tarayıcı sekme ikonu. Çoğu masaüstü tarayıcısında kullanılan birincil favicon boyutu.',
    usageNote: 'Tarayıcı sekme ikonu (standart), Windows kısayolları',
    icon: 'Monitor',
    accept: '.png',
    dimensions: '32 × 32 piksel',
    color: 'bg-violet-50 border-violet-200 text-violet-700',
    iconBg: 'bg-violet-100 text-violet-600',
  },
  {
    id: 'apple-touch-icon',
    fileName: 'apple-touch-icon.png',
    label: 'Apple Touch Icon',
    description: 'iPhone ve iPad\'de "Ana Ekrana Ekle" özelliği kullanıldığında görünen ikon. iOS Safari tarafından kullanılır.',
    usageNote: 'iOS ana ekran ikonu, iPad ana ekranı, Safari yer imleri',
    icon: 'Smartphone',
    accept: '.png',
    dimensions: '180 × 180 piksel',
    color: 'bg-gray-50 border-gray-200 text-gray-700',
    iconBg: 'bg-gray-100 text-gray-600',
  },
  {
    id: 'icon-192',
    fileName: 'icon-192x192.png',
    label: 'PWA İkon 192x192',
    description: 'Android cihazlarda ve Chrome\'da ana ekrana eklendiğinde kullanılan PWA ikonu. Google arama sonuçlarında da görünür.',
    usageNote: 'Android ana ekranı, Chrome PWA, Google arama sonuçları',
    icon: 'Smartphone',
    accept: '.png',
    dimensions: '192 × 192 piksel',
    color: 'bg-green-50 border-green-200 text-green-700',
    iconBg: 'bg-green-100 text-green-600',
  },
  {
    id: 'icon-192-maskable',
    fileName: 'icon-192x192-maskable.png',
    label: 'PWA Maskable İkon 192x192',
    description: 'Android\'in uyarlanabilir ikon sistemi için tasarlanmış versiyon. Farklı şekillerde (daire, kare, damla) kırpılabilir. İçerik %80 güvenli alana sığmalıdır.',
    usageNote: 'Android uyarlanabilir ikonlar, farklı cihaz şekilleri',
    icon: 'Layers',
    accept: '.png',
    dimensions: '192 × 192 piksel (güvenli alan: 153x153)',
    color: 'bg-teal-50 border-teal-200 text-teal-700',
    iconBg: 'bg-teal-100 text-teal-600',
  },
  {
    id: 'icon-512',
    fileName: 'icon-512x512.png',
    label: 'PWA İkon 512x512',
    description: 'Yüksek çözünürlüklü ekranlar ve PWA splash screen için kullanılan büyük ikon. Play Store ve uygulama mağazalarında da kullanılabilir.',
    usageNote: 'PWA splash screen, yüksek DPI ekranlar, uygulama mağazası',
    icon: 'Image',
    accept: '.png',
    dimensions: '512 × 512 piksel',
    color: 'bg-orange-50 border-orange-200 text-orange-700',
    iconBg: 'bg-orange-100 text-orange-600',
  },
  {
    id: 'icon-512-maskable',
    fileName: 'icon-512x512-maskable.png',
    label: 'PWA Maskable İkon 512x512',
    description: 'Büyük boyutlu uyarlanabilir ikon. Android\'de farklı launcher\'larda (daire, kare, vb.) düzgün görünmesi için kenar boşluklu tasarlanmalıdır.',
    usageNote: 'Android büyük uyarlanabilir ikonlar, tablet ana ekranı',
    icon: 'Layers',
    accept: '.png',
    dimensions: '512 × 512 piksel (güvenli alan: 409x409)',
    color: 'bg-rose-50 border-rose-200 text-rose-700',
    iconBg: 'bg-rose-100 text-rose-600',
  },
];

const BUCKET_NAME = 'logo-assets';

const ActiveLogos = ({ refreshTrigger }) => {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  const fetchLogos = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.storage
        ?.from(BUCKET_NAME)
        ?.list('logos', { sortBy: { column: 'name', order: 'asc' } });

      if (error) throw error;

      const logosWithUrls = (data || [])
        ?.filter(f => f?.name && f?.name !== '.emptyFolderPlaceholder')
        ?.map(file => {
          const { data: urlData } = supabase?.storage
            ?.from(BUCKET_NAME)
            ?.getPublicUrl(`logos/${file?.name}`);
          const config = LOGO_CONFIGS?.find(c => c?.fileName === file?.name);
          return {
            id: file?.name,
            name: file?.name,
            label: config?.label || file?.name,
            icon: config?.icon || 'Image',
            iconBg: config?.iconBg || 'bg-gray-100 text-gray-600',
            color: config?.color || 'bg-gray-50 border-gray-200 text-gray-700',
            dimensions: config?.dimensions || '',
            publicUrl: urlData?.publicUrl,
            updatedAt: file?.updated_at || file?.created_at,
          };
        });

      setLogos(logosWithUrls);
    } catch (err) {
      console.error('Aktif logolar yüklenemedi:', err);
      setLogos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogos();
  }, [fetchLogos, refreshTrigger]);

  const handleCopy = (url, id) => {
    navigator?.clipboard?.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Icon name="CheckCircle2" size={20} className="text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Aktif Logolar</h3>
            <p className="text-xs text-muted-foreground">Supabase Storage'a yüklenmiş ve aktif olan logolar</p>
          </div>
        </div>
        <button
          onClick={fetchLogos}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted text-foreground rounded-lg text-xs font-medium hover:bg-muted/80 transition-colors"
          title="Yenile"
        >
          <Icon name="RefreshCw" size={13} />
          Yenile
        </button>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="flex items-center justify-center py-10 gap-3">
            <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <span className="text-sm text-muted-foreground">Logolar yükleniyor...</span>
          </div>
        ) : logos?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
              <Icon name="ImageOff" size={24} className="text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Henüz logo yüklenmedi</p>
              <p className="text-xs text-muted-foreground mt-1">Aşağıdaki kartları kullanarak logo yükleyebilirsiniz</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
            {logos?.map(logo => (
              <div key={logo?.id} className="group relative bg-muted/30 border border-border rounded-xl overflow-hidden hover:border-primary/40 hover:shadow-sm transition-all">
                {/* Preview */}
                <div className="aspect-square flex items-center justify-center bg-[repeating-conic-gradient(#f0f0f0_0%_25%,#ffffff_0%_50%)] bg-[length:16px_16px] p-3">
                  {logo?.name?.endsWith('.ico') ? (
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${logo?.iconBg}`}>
                      <Icon name={logo?.icon} size={20} />
                    </div>
                  ) : (
                    <img
                      src={logo?.publicUrl}
                      alt={logo?.label}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  )}
                  {!logo?.name?.endsWith('.ico') && (
                    <div className={`w-10 h-10 rounded-lg items-center justify-center ${logo?.iconBg} hidden`}>
                      <Icon name={logo?.icon} size={20} />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="px-3 py-2 border-t border-border">
                  <p className="text-xs font-semibold text-foreground truncate" title={logo?.label}>{logo?.label}</p>
                  <p className="text-[10px] text-muted-foreground font-mono truncate mt-0.5" title={logo?.name}>{logo?.name}</p>
                  {logo?.dimensions && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">{logo?.dimensions}</p>
                  )}
                </div>

                {/* Actions overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <a
                    href={logo?.publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
                    title="Görüntüle"
                    onClick={e => e?.stopPropagation()}
                  >
                    <Icon name="Eye" size={14} />
                  </a>
                  <button
                    onClick={() => handleCopy(logo?.publicUrl, logo?.id)}
                    className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
                    title="URL Kopyala"
                  >
                    <Icon name={copiedId === logo?.id ? 'Check' : 'Copy'} size={14} />
                  </button>
                </div>

                {/* Active badge */}
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm">
                  <Icon name="Check" size={10} className="text-white" />
                </div>
              </div>
            ))}
          </div>
        )}

        {logos?.length > 0 && (
          <p className="text-xs text-muted-foreground mt-4 text-center">
            Toplam <span className="font-semibold text-foreground">{logos?.length}</span> aktif logo · Supabase Storage'da saklanıyor
          </p>
        )}
      </div>
    </div>
  );
};

const LogoUploadCard = ({ config, onUpload }) => {
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'uploading' | 'success' | 'error' | null
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  const uploadToSupabase = async (file) => {
    const fileExt = file?.name?.split('.')?.pop()?.toLowerCase() || 'png';
    const storagePath = `logos/${config?.fileName}`;

    const { data, error } = await supabase?.storage
      ?.from(BUCKET_NAME)
      ?.upload(storagePath, file, {
        cacheControl: '3600',
        upsert: true,
        contentType: file?.type || 'application/octet-stream',
      });

    if (error) throw error;

    const { data: urlData } = supabase?.storage
      ?.from(BUCKET_NAME)
      ?.getPublicUrl(data?.path);

    return urlData?.publicUrl;
  };

  const handleFile = async (file) => {
    if (!file) return;

    const isIco = config?.accept === '.ico';
    const isPng = config?.accept === '.png';

    if (isIco && !file?.name?.endsWith('.ico')) {
      setUploadStatus('error');
      setErrorMessage('Geçersiz dosya formatı. Beklenen: .ico');
      setTimeout(() => { setUploadStatus(null); setErrorMessage(''); }, 3000);
      return;
    }
    if (isPng && !file?.type?.startsWith('image/')) {
      setUploadStatus('error');
      setErrorMessage('Geçersiz dosya formatı. Beklenen: .png');
      setTimeout(() => { setUploadStatus(null); setErrorMessage(''); }, 3000);
      return;
    }

    setUploadStatus('uploading');
    setErrorMessage('');

    try {
      // Set local preview for non-ico files
      if (!isIco) {
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e?.target?.result);
        reader?.readAsDataURL(file);
      }

      const publicUrl = await uploadToSupabase(file);
      setUploadedUrl(publicUrl);
      setUploadStatus('success');
      onUpload(config?.id, config?.fileName, file, publicUrl);
      setTimeout(() => setUploadStatus(null), 4000);
    } catch (err) {
      console.error('Upload error:', err);
      setUploadStatus('error');
      setErrorMessage(err?.message || 'Yükleme başarısız oldu');
      setPreview(null);
      setTimeout(() => { setUploadStatus(null); setErrorMessage(''); }, 4000);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    handleFile(file);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleInputChange = (e) => {
    const file = e?.target?.files?.[0];
    handleFile(file);
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className={`px-4 py-3 border-b border-border flex items-center gap-3 ${config?.color}`}>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${config?.iconBg}`}>
          <Icon name={config?.icon} size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm">{config?.label}</h3>
          <p className="text-xs opacity-75 font-mono">{config?.fileName}</p>
        </div>
        <span className="text-xs font-medium opacity-60 hidden sm:block">{config?.dimensions}</span>
      </div>
      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Description */}
        <p className="text-xs text-muted-foreground leading-relaxed">{config?.description}</p>

        {/* Usage note */}
        <div className="flex items-start gap-2 bg-muted/50 rounded-lg px-3 py-2">
          <Icon name="MapPin" size={12} className="text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Kullanım:</span> {config?.usageNote}</p>
        </div>

        {/* Upload area */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => uploadStatus !== 'uploading' && fileInputRef?.current?.click()}
          className={`relative border-2 border-dashed rounded-lg p-4 transition-all text-center
            ${uploadStatus !== 'uploading' ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}
            ${isDragging ? 'border-primary bg-primary/5 scale-[1.01]' : 'border-border hover:border-primary/50 hover:bg-muted/30'}
            ${uploadStatus === 'success' ? 'border-green-400 bg-green-50' : ''}
            ${uploadStatus === 'error' ? 'border-red-400 bg-red-50' : ''}
            ${uploadStatus === 'uploading' ? 'border-blue-400 bg-blue-50' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={config?.accept}
            onChange={handleInputChange}
            className="hidden"
            disabled={uploadStatus === 'uploading'}
          />

          {uploadStatus === 'uploading' ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
              <p className="text-xs text-blue-600 font-medium">Yükleniyor...</p>
            </div>
          ) : preview && uploadStatus !== 'error' ? (
            <div className="flex flex-col items-center gap-2">
              <img src={preview} alt={config?.label} className="w-12 h-12 object-contain rounded" />
              <p className="text-xs text-green-600 font-medium">✓ Yüklendi</p>
              <p className="text-xs text-muted-foreground">Değiştirmek için tıkla</p>
            </div>
          ) : uploadStatus === 'success' && config?.accept === '.ico' ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Icon name="CheckCircle" size={24} className="text-green-600" />
              </div>
              <p className="text-xs text-green-600 font-medium">✓ ICO dosyası yüklendi</p>
            </div>
          ) : uploadStatus === 'error' ? (
            <div className="flex flex-col items-center gap-2">
              <Icon name="XCircle" size={24} className="text-red-500" />
              <p className="text-xs text-red-600 font-medium">{errorMessage || 'Yükleme başarısız'}</p>
              <p className="text-xs text-muted-foreground">Tekrar denemek için tıkla</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                <Icon name="Upload" size={18} className="text-muted-foreground" />
              </div>
              <p className="text-xs text-foreground font-medium">Dosya seç veya sürükle</p>
              <p className="text-xs text-muted-foreground">{config?.accept?.toUpperCase()} formatı</p>
            </div>
          )}
        </div>

        {/* Uploaded URL display */}
        {uploadedUrl && uploadStatus !== 'error' && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <Icon name="Link" size={12} className="text-green-600 flex-shrink-0" />
            <a
              href={uploadedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-green-700 font-mono truncate hover:underline flex-1"
              title={uploadedUrl}
            >
              {uploadedUrl}
            </a>
            <button
              onClick={(e) => { e?.stopPropagation(); navigator?.clipboard?.writeText(uploadedUrl); }}
              className="flex-shrink-0 text-green-600 hover:text-green-800"
              title="URL'yi kopyala"
            >
              <Icon name="Copy" size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ThemeLogoManagement = () => {
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [showGenerator, setShowGenerator] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleUpload = (id, fileName, file, publicUrl) => {
    setUploadedFiles(prev => ({ ...prev, [id]: { fileName, file, publicUrl, uploadedAt: new Date() } }));
    setRefreshTrigger(prev => prev + 1);
  };

  const uploadedCount = Object.keys(uploadedFiles)?.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Icon name="Palette" size={22} className="text-primary" />
              Tema & Logo Yönetimi
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Favicon, PWA ikonları ve uygulama logolarını buradan güncelleyebilirsiniz.
            </p>
          </div>
          {uploadedCount > 0 && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
              <Icon name="CheckCircle" size={16} className="text-green-600" />
              <span className="text-sm text-green-700 font-medium">{uploadedCount} dosya Supabase'e yüklendi</span>
            </div>
          )}
        </div>

        {/* Info banner */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
          <Icon name="Info" size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-800 space-y-1">
            <p className="font-semibold">Nasıl çalışır?</p>
            <p>Her logo kartında "Dosya seç" alanına tıklayarak veya dosyayı sürükleyerek ilgili ikonu yükleyin. Dosya seçildiği anda otomatik olarak <strong>Supabase Storage</strong>'a yüklenir ve gerçek URL gösterilir. Kaydet butonu gerekmez.</p>
          </div>
        </div>
      </div>

      {/* Active Logos Section */}
      <ActiveLogos refreshTrigger={refreshTrigger} />

      {/* Icon Generator Section */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Wand2" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">İkon Üretici</h3>
              <p className="text-xs text-muted-foreground">Logo dosyanızdan tüm gerekli formatları otomatik oluşturun</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/icon-generator.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Icon name="ExternalLink" size={14} />
              Yeni Sekmede Aç
            </a>
            <button
              onClick={() => setShowGenerator(prev => !prev)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors"
            >
              <Icon name={showGenerator ? 'ChevronUp' : 'ChevronDown'} size={14} />
              {showGenerator ? 'Gizle' : 'Burada Aç'}
            </button>
          </div>
        </div>

        {/* Feature highlights */}
        <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: 'FileImage', label: 'favicon.ico', desc: '16+32+48px çoklu boyut', color: 'text-green-600 bg-green-50' },
            { icon: 'Monitor', label: 'Favicon PNG', desc: '16×16 ve 32×32 piksel', color: 'text-blue-600 bg-blue-50' },
            { icon: 'Smartphone', label: 'Apple Touch', desc: '180×180 iOS ikonu', color: 'text-gray-600 bg-gray-50' },
            { icon: 'Layers', label: 'PWA İkonlar', desc: '192×192 ve 512×512 (maskable)', color: 'text-purple-600 bg-purple-50' },
          ]?.map((f, i) => (
            <div key={i} className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg">
              <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${f?.color}`}>
                <Icon name={f?.icon} size={14} />
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground">{f?.label}</p>
                <p className="text-xs text-muted-foreground">{f?.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Workflow steps */}
        <div className="px-5 pb-4">
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <p className="text-xs font-semibold text-primary mb-3 flex items-center gap-1.5">
              <Icon name="ListOrdered" size={13} />
              Kullanım Adımları
            </p>
            <ol className="space-y-2">
              {[
                'İkon Üretici\'yi açın (yukarıdaki butonlardan birini kullanın)',
                'Logo dosyanızı yükleyin (PNG, SVG, JPG desteklenir)',
                'Arka plan rengi ve stilini ayarlayın',
                '"İkonları Oluştur" butonuna tıklayın',
                '"Tümünü İndir (ZIP)" ile tüm dosyaları indirin',
                'ZIP içindeki dosyaları aşağıdaki kartlara yükleyin — otomatik Supabase\'e kaydedilir',
              ]?.map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center flex-shrink-0 font-bold text-[10px]">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Embedded iframe */}
        {showGenerator && (
          <div className="border-t border-border">
            <iframe
              src="/icon-generator.html"
              title="İkon Üretici"
              className="w-full"
              style={{ height: '700px', border: 'none' }}
              sandbox="allow-scripts allow-same-origin allow-downloads"
            />
          </div>
        )}
      </div>

      {/* Upload grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {LOGO_CONFIGS?.map((config) => (
          <LogoUploadCard
            key={config?.id}
            config={config}
            onUpload={handleUpload}
          />
        ))}
      </div>

      {/* Summary / Instructions */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="BookOpen" size={16} className="text-primary" />
          Dosya Yerleştirme Rehberi
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
          <div className="space-y-2">
            <p className="font-medium text-foreground">Favicon dosyaları:</p>
            <ul className="space-y-1 text-xs">
              <li className="flex items-center gap-2"><code className="bg-muted px-1.5 py-0.5 rounded font-mono text-foreground">favicon.ico</code> → <span>public/ klasörüne</span></li>
              <li className="flex items-center gap-2"><code className="bg-muted px-1.5 py-0.5 rounded font-mono text-foreground">favicon-16x16.png</code> → <span>public/icons/ klasörüne</span></li>
              <li className="flex items-center gap-2"><code className="bg-muted px-1.5 py-0.5 rounded font-mono text-foreground">favicon-32x32.png</code> → <span>public/icons/ klasörüne</span></li>
              <li className="flex items-center gap-2"><code className="bg-muted px-1.5 py-0.5 rounded font-mono text-foreground">apple-touch-icon.png</code> → <span>public/icons/ klasörüne</span></li>
            </ul>
          </div>
          <div className="space-y-2">
            <p className="font-medium text-foreground">PWA ikon dosyaları:</p>
            <ul className="space-y-1 text-xs">
              <li className="flex items-center gap-2"><code className="bg-muted px-1.5 py-0.5 rounded font-mono text-foreground">icon-192x192.png</code> → <span>public/icons/ klasörüne</span></li>
              <li className="flex items-center gap-2"><code className="bg-muted px-1.5 py-0.5 rounded font-mono text-foreground">icon-192x192-maskable.png</code> → <span>public/icons/ klasörüne</span></li>
              <li className="flex items-center gap-2"><code className="bg-muted px-1.5 py-0.5 rounded font-mono text-foreground">icon-512x512.png</code> → <span>public/icons/ klasörüne</span></li>
              <li className="flex items-center gap-2"><code className="bg-muted px-1.5 py-0.5 rounded font-mono text-foreground">icon-512x512-maskable.png</code> → <span>public/icons/ klasörüne</span></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeLogoManagement;
